import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REASONS = ["New stock", "Damage", "Lost", "Found", "Correction", "Return", "Supplier shipment", "Other"];
const NOTES_REQUIRED = new Set(["Damage", "Lost", "Other", "Correction"]);

export const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Request failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
};

export const AdjustInventoryModal = ({ row, onClose, onDone }) => {
  const inv = row.inventory;
  const [direction, setDirection] = useState("increase");
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("New stock");
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const delta = (direction === "increase" ? 1 : -1) * Math.abs(parseInt(amount || "0", 10));
  const next = inv.quantity_on_hand + delta;
  const notesNeeded = delta < 0 && NOTES_REQUIRED.has(reason) && !notes.trim();
  const invalid = !delta || next < 0 || notesNeeded || (inv.inventory_type === "UNIQUE_ITEM" && next > 1);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/inventory/adjust", { inventory_id: inv.inventory_id, adjustment: delta, reason, notes, confirm: true });
      toast.success(`Inventory adjusted: ${inv.quantity_on_hand} → ${next}`);
      onDone();
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="adjust-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 mt-10 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-brand-red font-black uppercase tracking-widest text-xs">Adjust inventory</div>
            <div className="font-heading text-xl text-brand-navy">{row.product.product_name}</div>
            <div className="text-xs text-slate-500">SKU {row.product.sku} · {row.location_name} · {inv.inventory_type}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-brand-red font-bold" data-testid="adjust-close">Close</button>
        </div>

        {!confirming ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              {["increase", "decrease"].map((d) => (
                <button key={d} data-testid={`adjust-${d}`} onClick={() => { setDirection(d); setReason(d === "increase" ? "New stock" : "Correction"); }}
                  className={`h-11 rounded-lg font-bold border ${direction === d ? "bg-brand-navy text-white border-brand-navy" : "border-slate-200"}`}>
                  {d === "increase" ? "+ Increase" : "− Decrease"}
                </button>
              ))}
            </div>
            <label className="block">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Quantity</span>
              <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adjust-amount"
                className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Reason</span>
              <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason"
                className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white">
                {REASONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Notes {notesNeeded && <span className="text-brand-red">(required)</span>}</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="adjust-notes"
                className="mt-1 w-full rounded-lg border border-slate-200 p-2" />
            </label>
            {next < 0 && <div className="text-sm font-bold text-brand-red" data-testid="adjust-error">Cannot go below zero (available: {inv.quantity_available}).</div>}
            {inv.inventory_type === "UNIQUE_ITEM" && next > 1 && <div className="text-sm font-bold text-brand-red">Unique items can only hold 1 unit.</div>}
            <button disabled={invalid} onClick={() => setConfirming(true)} data-testid="adjust-review-btn"
              className="w-full h-11 rounded-full bg-brand-navy text-white font-bold disabled:opacity-50">Review adjustment</button>
          </>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center" data-testid="adjust-confirmation">
              <Stat label="Current" value={inv.quantity_on_hand} />
              <Stat label="Adjustment" value={(delta > 0 ? "+" : "") + delta} accent={delta < 0 ? "text-brand-red" : "text-emerald-600"} />
              <Stat label="New" value={next} />
            </div>
            <div className="text-sm text-slate-600">Reason: <b>{reason}</b>{notes && <> · Notes: {notes}</>}</div>
            <div className="flex gap-2">
              <button onClick={submit} disabled={busy} data-testid="adjust-confirm-btn"
                className="flex-1 h-11 rounded-full bg-brand-red text-white font-bold disabled:opacity-60">{busy ? "Saving..." : "Confirm adjustment"}</button>
              <button onClick={() => setConfirming(false)} className="h-11 px-4 rounded-full border border-slate-200 font-bold">Back</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent = "text-brand-navy" }) => (
  <div className="bg-slate-50 rounded-xl p-3">
    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`font-heading text-2xl ${accent}`}>{value}</div>
  </div>
);
